import flowRight from 'lodash/flowRight';
import { QueryString } from '@grandvisionhq/utils';
import { FacetId, isFacetVisible } from '../../types.js';

const PRICE_FROM_PARAM = FacetId.price + 'From';
const PRICE_TO_PARAM = FacetId.price + 'To';
const QUERY_KEYS = [
    'q',
    'page',
    'sortBy',
    PRICE_FROM_PARAM,
    PRICE_TO_PARAM
];
function getQueryParamsFromUri(uri = '') {
    return QueryString.parse(uri.split('?')[1] || '');
}
function getQueryKey(key1, facets) {
    const keys = QUERY_KEYS.concat(facets.map((f)=>f.id
    ));
    const lookup = keys.reduce((result, key)=>{
        result[key.toLowerCase()] = key;
        return result;
    }, {});
    return lookup[key1.toLowerCase()];
}
function getNonQueryObject(uri, { facets  }) {
    return Object.entries(getQueryParamsFromUri(uri)).reduce((result, [key, value])=>{
        if (!getQueryKey(key, facets)) {
            result[key] = value;
        }
        return result;
    }, {});
}
function queryStringToQueryObject(uri, { facets  }) {
    return Object.entries(getQueryParamsFromUri(uri)).reduce((result, [key, value])=>{
        const translatedKey = getQueryKey(key, facets);
        if (translatedKey) {
            result[translatedKey] = value;
        }
        return result;
    }, {});
}
function searchStateToQueryObject(searchState, { facets , pageSize , sortIndices , presetQueryObject  }) {
    const facetsVisible = facets.filter(isFacetVisible);
    return flowRight(addPage, addSort, addQuery, addPriceRange, addRefinementListAttributes)({});
    function addPage(queryObject) {
        var ref;
        const { length =pageSize , page =1 , offset =0 ,  } = searchState.configure || {
            length: pageSize,
            page: 1,
            offset: 0
        };
        let newPageIndex = page;
        if (length > pageSize) {
            //Server.
            newPageIndex = offset === 0 ? page + 1 : page;
        }
        if (offset === 0 && length > pageSize) {
            // Client. We're not on page one so we calculate the page
            newPageIndex = Math.ceil(length / pageSize);
        }
        if (offset === 0 && length === pageSize && ((ref = searchState.configure) === null || ref === void 0 ? void 0 : ref.filters)) {
            // If a filter is enabled we set the page back to one
            searchState.configure.page = 1;
            newPageIndex = 1;
        }
        if (newPageIndex > 1) {
            queryObject.page = newPageIndex.toString();
        }
        return queryObject;
    }
    function addSort(queryObject) {
        var ref;
        const { sortBy  } = searchState;
        const sortIndex = sortIndices.find((x)=>x.value === sortBy
        );
        const isDefaultSortIndex = presetQueryObject.sortBy ? sortIndex && sortIndex.id === presetQueryObject.sortBy : sortIndex && sortIndex.id === ((ref = sortIndices[0]) === null || ref === void 0 ? void 0 : ref.id);
        if (sortIndex && sortIndex.id && !isDefaultSortIndex) {
            queryObject.sortBy = sortIndex.id;
        }
        return queryObject;
    }
    function addQuery(queryObject) {
        if (!presetQueryObject.q && searchState.query) {
            queryObject.q = searchState.query;
        }
        return queryObject;
    }
    function addPriceRange(queryObject) {
        const facet = facetsVisible.find((f)=>f.id === FacetId.price
        );
        const value = facet && searchState.range && searchState.range[facet.attribute];
        if (facet && value) {
            const [min, max] = value.split(':').map(Number);
            min && (queryObject[PRICE_FROM_PARAM] = Math.round(min / 100).toString());
            max && (queryObject[PRICE_TO_PARAM] = Math.round(max / 100).toString());
        }
        return queryObject;
    }
    function addRefinementListAttributes(queryObject) {
        Object.entries(searchState.refinementList || {}).forEach(([attribute, value])=>{
            const facet = facetsVisible.find((f)=>f.attribute === attribute
            );
            if (facet && value.length) {
                queryObject[facet.id] = value;
            }
        }, {});
        return queryObject;
    }
}
function queryObjectToSearchState(queryObject, { facets , pageSize , sortIndices , presetQueryObject  }) {
    return flowRight(addPage, addSort, addQuery, addPriceRange, addRefinementListAttributes)({
        configure: {},
        refinementList: {},
        range: {}
    });
    function addPage(searchState) {
        var ref;
        if (queryObject.page && ((ref = searchState.configure) === null || ref === void 0 ? void 0 : ref.offset) !== 0) {
            const page = Math.max(1, parseInt(queryObject.page, 10));
            searchState.configure.page = page;
            searchState.configure.offset = Math.min((page - 1) * pageSize, 999);
            searchState.configure.length = Math.min(page * pageSize, 999);
        }
        return searchState;
    }
    function addSort(searchState) {
        const { sortBy  } = queryObject;
        const { value =null  } = sortIndices.find((x)=>x.id === sortBy
        ) || {};
        if (value) {
            searchState.sortBy = value;
        }
        return searchState;
    }
    function addQuery(searchState) {
        const query = presetQueryObject.q || queryObject.q;
        if (query) {
            searchState.query = query;
        }
        return searchState;
    }
    function addPriceRange(searchState) {
        const facet = facets.find((f)=>f.id === FacetId.price
        );
        const priceKeys = [
            queryObject[PRICE_FROM_PARAM],
            queryObject[PRICE_TO_PARAM], 
        ];
        const [min, max] = priceKeys.map((x)=>(parseFloat(x || '') || 0) * 100
        );
        if (facet && (min || max)) {
            const rangeValue = `${min || ''}:${max || ''}`;
            searchState.range[facet.attribute] = rangeValue;
        }
        return searchState;
    }
    function addRefinementListAttributes(searchState) {
        Object.entries(queryObject).forEach(([id, values])=>{
            values = [].concat(values);
            const facet = values.length && facets.find((f)=>f.id === id
            );
            if (facet) {
                searchState.refinementList[facet.attribute] = values;
            }
        });
        return searchState;
    }
}
function excludeHiddenFacets(queryObject, { facets  }) {
    const visibleFacets = facets.filter(isFacetVisible).map((f)=>f.id
    );
    return Object.entries(queryObject).reduce((result, [key, value])=>{
        // Always include QUERY_KEYS (non-facet parameters)
        if (QUERY_KEYS.includes(key)) {
            result[key] = value;
            return result;
        }
        if (visibleFacets.includes(key)) {
            result[key] = value;
        }
        return result;
    }, {});
}

export { PRICE_FROM_PARAM, PRICE_TO_PARAM, excludeHiddenFacets, getNonQueryObject, queryObjectToSearchState, queryStringToQueryObject, searchStateToQueryObject };
//# sourceMappingURL=query-string.js.map
